import React, { useState, useContext } from "react"
import formatVideo from "../../utils/organism-formatting/formatVideo"
import { CTAButtons } from "terra-one"
import { LayoutContext } from "../Layout/Layout"
import { updateDownloadLink } from "../../utils/organism-formatting/formatPageLinks"
import { Link } from "gatsby-link";
import { useStaticQuery, graphql } from "gatsby"

const CTAButtonsConnector = ({
  data = {},
  locale = "en",
  videoProgress,
  setVideoProgress,
  currentSpaceId,
  products,
  siteIdentifier,
}) => {
  const { homepageSlug } = useContext(LayoutContext)
  const [override, setOverride] = useState(false)
  const { alignment, variant, ctaButtons } = data

  const englishData = useStaticQuery(graphql`
    query {
      allContentfulCtaButtons(filter: { node_locale: { eq: "en-US" }}) {
        nodes {
          contentfulId: contentful_id
          ctaButtons {
            id
            ... on ContentfulLink {
              ...Link
            }
          }
        }
      }
    }
  `)
  const englishCtaButtons = englishData?.allContentfulCtaButtons?.nodes?.find(node => node?.contentfulId === data?.contentfulId);
  const parsedButtons = formatCTAButtons(
    ctaButtons,
    locale,
    override,
    setOverride,
    homepageSlug,
    currentSpaceId,
    products,
    siteIdentifier,
    englishCtaButtons
  )
  return (
    <CTAButtons
      alignment={alignment}
      variant={variant}
      buttons={parsedButtons}
      contentfulId={data?.contentfulId ? data?.contentfulId : null}
    />
  )
}

export default CTAButtonsConnector

export const formatCTAButtons = (
  ctaButtons = [],
  locale = "en",
  override,
  setOverride,
  homepageSlug,
  currentSpaceId,
  products,
  siteIdentifier,
  englishCtaButtons = null
) => {
  if (!ctaButtons || !ctaButtons?.length) {
    return null
  }
  const ctaButtonsFormatted = ctaButtons?.map((button, i) => {
    const contentfulId = button?.contentfulId

    if (button?.url?.product?.sys?.urn) {
      return createProductTrialPageLink(
        locale,
        button,
      )
    }

    if (button?.url?.type && button?.url?.type === "ContentfulDownloadLink") {
      return {
        text: button?.text,
        onClick: () =>
          window.open(
            updateDownloadLink(button?.url?.realLink?.url, currentSpaceId),
            "_blank"
          ),
      }
    }
    // if the locale isn't english, add an english label as data-attribute for dataLayer tracking events
    const englishTextLabel = (locale != "en" && englishCtaButtons?.ctaButtons?.length > 0) ? englishCtaButtons?.ctaButtons[i]?.text : button?.text;

    let videoContent

    if (button?.video) {
      const videoData = formatVideo(button?.video)

      const video = videoData?.video

      const thumbnail = videoData?.thumbnail?.focalPointImage?.url
        ? videoData?.thumbnail
        : null

      videoContent = {
        transcript: videoData?.transcript,
        transcriptText: videoData?.transcriptText,
        closeTranscript: videoData?.closeTranscript,
        video,
        thumbnail,
        variant: "variant_7",
        videoProgress: button?.videoProgress,
        setVideoProgress: button?.setVideoProgress,
      }
    }

    // TODO - Update this to use formatPageLinks like other components
    let text

    if (button?.text) {
      text = button.text
    }

    if (!button?.text) {
      text = button?.url?.text
    }

    let prefix = ``

    const type = button?.url?.contentType
      ? button?.url?.contentType
      : button?.url?.internal?.type
      ? button?.url?.internal?.type
      : ""

    if (button?.url?.__typename === "ContentfulProductDetailPage"){
      return createProductDetailPageLink(locale, button)
    }

    if (type && type === "article") {
      prefix = `articles/`
    } else if (type && type === "event") {
      prefix = `events/`
    } else if (type && type === "prose") {
      prefix = `prose/`
    } else if (type && type === "ContentfulNews") {
      prefix = `news/`
    } else if (
      type &&
      type === "ContentfulProductPage" &&
      !button?.url?.url?.includes("products/")
    ) {
      prefix = `products/`
    }

    const url = button?.internalLink ? button?.internalLink : button?.url?.url

    if (!url) {
      return {
        text,
        setOverride,
        override,
        video: videoContent,
        contentfulId,
        anchor: button?.anchor,
      }
    }

    const destination = button?.url?.realLink?.url
      ? null
      : button?.url?.url?.startsWith("http")
      ? url
      : url === homepageSlug
      ? `/${locale?.toLowerCase()}/${prefix}`
      : `/${locale?.toLowerCase()}/${prefix}${url}`

    return {
      contentfulId,
      text,
      url: destination,
      anchor: button?.anchor,
      englishTextLabel: englishTextLabel
    }
  })

  const filterOutForUndefinedBtns = ctaButtonsFormatted.filter(btn => {
    return btn !== undefined
  })
  return filterOutForUndefinedBtns
}

export const createProductTrialPageLink = (
  locale,
  button
) => {
  if (!button) {
    return
  }
  return {
    url: button?.url?.productName
      ? `/${locale}/products/${button?.url?.productName?.split(" ")
          ?.join("-")
          ?.toLowerCase()}/trial`
      : `/${locale}/products/${button?.productName?.split(" ")
          ?.join("-")
          ?.toLowerCase()}/trial`,
    text: button?.text
      ? button?.text
      : button?.url?.productName
      ? `Start a trial of ${button?.url?.productName}`
      : `Start a trial of ${button?.productName}`,
    type: "link",
    external: false,
    publicPage: true,
    link: Link,
  }
}

export const createProductDetailPageLink = (locale, button) => {
  if (!button) {
    return
  }
  return {
    url: button?.productName
      ? `/${locale}/products/${button?.productName
          ?.split(" ")
          ?.join("-")
          ?.toLowerCase()}/buy`
      : button?.url?.url ? `/${locale}/products/${button?.url?.url
          ?.split(" ")
          ?.join("-")
          ?.toLowerCase()}/buy` 
      : `/${locale}/products/${button?.url?.productName 
          ?.split(" ")
          ?.join("-")
          ?.toLowerCase()}/buy`,

    text: button?.text
      ? button?.text
      : button?.url?.url
      ? `Buy ${button?.url?.url}`
      : `Buy Now`,
    type: "link",
    external: false,
    publicPage: true,
    link: Link,
  }
}